import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type Lead, createLead, fetchContacts, fetchLeads, updateLead } from '../api/resources';

export function LeadsPage() {
  const { data: contacts } = useQuery({ queryKey: ['contacts'], queryFn: fetchContacts });
  const { data: leads } = useQuery({ queryKey: ['leads'], queryFn: fetchLeads });
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState({
    contactId: '',
    status: 'new',
    source: '',
    score: 50,
    notes: '',
  });
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editState, setEditState] = useState({
    status: 'new',
    source: '',
    score: 50,
    notes: '',
  });

  const createMutation = useMutation({
    mutationFn: createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setFormState({ contactId: '', status: 'new', source: '', score: 50, notes: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: {
        status: string;
        source?: string;
        score?: number;
        notes?: string;
      };
    }) => updateLead(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setEditingLead(null);
    },
  });

  const openEditor = (lead: Lead) => {
    setEditingLead(lead);
    setEditState({
      status: lead.status,
      source: lead.source ?? '',
      score: lead.score,
      notes: lead.notes ?? '',
    });
  };

  const closeEditor = () => setEditingLead(null);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Leads</h1>
          <p>Qualify and convert new interest into revenue.</p>
        </div>
        <div className="page-header-actions">
          <Link className="btn btn--secondary" to="/import?type=leads">
            Import CSV
          </Link>
        </div>
      </div>

      <div className="card">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!formState.contactId) return;
            createMutation.mutate({
              contactId: formState.contactId,
              status: formState.status,
              source: formState.source || undefined,
              score: formState.score,
              notes: formState.notes || undefined,
            });
          }}
        >
          <div className="form-grid">
            <label>
              Contact
              <select value={formState.contactId} onChange={(e) => setFormState((s) => ({ ...s, contactId: e.target.value }))} required>
                <option value="">Select contact...</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select value={formState.status} onChange={(e) => setFormState((s) => ({ ...s, status: e.target.value }))}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="proposal">Proposal</option>
                <option value="closed">Closed</option>
              </select>
            </label>
            <label>
              Score
              <input
                type="number"
                min={0}
                max={100}
                value={formState.score}
                onChange={(e) => setFormState((s) => ({ ...s, score: Number(e.target.value) }))}
              />
            </label>
          </div>
          <label>
            Source
            <input value={formState.source} onChange={(e) => setFormState((s) => ({ ...s, source: e.target.value }))} />
          </label>
          <label>
            Notes
            <textarea value={formState.notes} onChange={(e) => setFormState((s) => ({ ...s, notes: e.target.value }))} rows={3} />
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding…' : 'Add lead'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Status</th>
              <th>Source</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {leads?.map((lead) => (
              <tr key={lead.id} className="table-row" onClick={() => openEditor(lead)}>
                <td>{[lead.first_name, lead.last_name].filter(Boolean).join(' ')}</td>
                <td>{lead.status}</td>
                <td>{lead.source ?? '—'}</td>
                <td>{lead.score}</td>
              </tr>
            ))}
            {!leads?.length && (
              <tr>
                <td colSpan={4}>No leads found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingLead && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2>Edit lead</h2>
            <p className="modal-description">
              Fine-tune status and prioritization to keep your pipeline current.
            </p>
            <p className="modal-meta">
              Contact: {[editingLead.first_name, editingLead.last_name].filter(Boolean).join(' ') || editingLead.email}
            </p>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate({
                  id: editingLead.id,
                  payload: {
                    status: editState.status,
                    source: editState.source,
                    score: editState.score,
                    notes: editState.notes,
                  },
                });
              }}
            >
              <label>
                Status
                <select value={editState.status} onChange={(e) => setEditState((s) => ({ ...s, status: e.target.value }))}>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
              <div className="form-grid">
                <label>
                  Source
                  <input value={editState.source} onChange={(e) => setEditState((s) => ({ ...s, source: e.target.value }))} />
                </label>
                <label>
                  Score
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={editState.score}
                    onChange={(e) => setEditState((s) => ({ ...s, score: Number(e.target.value) }))}
                  />
                </label>
              </div>
              <label>
                Notes
                <textarea value={editState.notes} onChange={(e) => setEditState((s) => ({ ...s, notes: e.target.value }))} rows={3} />
              </label>
              <div className="form-actions">
                <button className="btn" type="button" onClick={closeEditor}>
                  Cancel
                </button>
                <button className="btn btn--primary" type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
